import { Outlet } from 'react-router';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import { TOP_STRIP_H } from '../../components/Frame';
import { TOGGLE_BAND } from '../../components/viewToggleMetrics';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { usePhoneChrome } from '../../shared/usePhoneChrome';
import { RouterLink } from './authUi';

/**
 * The frame around every account page.
 *
 * A single narrow column, the same on a laptop and a phone: these are forms,
 * and a form wider than about 420px is only a longer walk for the eye between
 * a label and the button. The page scrolls on its own rather than relying on
 * the body, because the main app locks the body to the height of the screen.
 */

/**
 * Over the app, which stays mounted underneath (AppShell). Nothing above
 * `#root` makes a stacking context, so without a z-index of its own App's
 * positioned layers would paint straight through this. The view toggle is the
 * one thing meant to sit above it, at 1270.
 */
const Page = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: 1260,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  background: BRAND.mocha,
  color: BRAND.parchment,
  display: 'flex',
  flexDirection: 'column',
});

/**
 * Says plainly that nothing here does anything yet.
 *
 * This branch exists so the flows can be looked at and argued about. Anyone
 * who types a real password into a preview deserves to be told first that it
 * goes nowhere.
 */
const Banner = styled('p')({
  margin: 0,
  padding: '9px 16px',
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: 13,
  lineHeight: 1.4,
  background: BRAND.dustyOlive,
  color: BRAND.parchment,
});

const Top = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  padding: '30px 20px 8px',
});

const Home = styled(RouterLink)({
  display: 'block',
  width: 132,
  color: BRAND.parchment,
  '&:hover': { opacity: 0.8 },
});

const Column = styled('main')({
  width: '100%',
  maxWidth: 420,
  margin: '0 auto',
  padding: '26px 20px 12px',
  boxSizing: 'border-box',
  flex: 1,
});

const Foot = styled('div')({
  padding: '10px 20px 34px',
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: 14,
});

export default function AccountLayout() {
  const phoneChrome = usePhoneChrome();
  return (
    /**
     * The toggle is drawn over this page and does not move for it, so the page
     * moves instead: down from under it on a desktop, where it crosses the
     * centred wordmark, and up off the band on a phone, where "Back to My
     * Climate Week" would otherwise sit under the pill.
     */
    <Page
      style={
        phoneChrome ? { paddingBottom: TOGGLE_BAND } : { paddingTop: TOP_STRIP_H }
      }
    >
      {/* <Banner role="note">
        <strong>Preview.</strong> Accounts aren&rsquo;t switched on yet — nothing entered here is
        saved or sent.
      </Banner> */}

      <Top>
        <Home to="/" aria-label="My Climate Week — back to all events">
          <Icon name="logo:mcw" size="100%" />
        </Home>
      </Top>

      <Column>
        <Outlet />
      </Column>

      <Foot>
        <RouterLink to="/">Back to My Climate Week</RouterLink>
      </Foot>
    </Page>
  );
}
